import { GraphicPrimitive } from './GraphicPrimitive';

export class Circle extends GraphicPrimitive {
  constructor(centerX, centerY, radius, color, fillColor, fillType, spacing, angle, segmentLength) {
    super(color, fillColor, fillType, spacing, angle, segmentLength, 'simple', 'Circle');
    this.centerX = centerX;
    this.centerY = centerY;
    this.radius = radius;
    this.m_isDrawing = false;
    this.m_hasSecondPoint = false;
    this.regenerateStitches();
  }

  toJSON() {
    return {
      ...super.toJSON(),
      centerX: this.centerX,
      centerY: this.centerY,
      radius: this.radius
    };
  }

  getControlPoints() {
    return [
      { x: this.centerX, y: this.centerY },
      { x: this.centerX + this.radius, y: this.centerY }
    ];
  }

  updateControlPoint(pointIndex, newX, newY) {
    if (pointIndex === 0) {
      // Move center
      this.centerX = newX;
      this.centerY = newY;
    } else if (pointIndex === 1) {
      // Adjust radius based on new point position
      const dx = newX - this.centerX;
      const dy = newY - this.centerY;
      this.radius = Math.sqrt(dx * dx + dy * dy);
    }
    this.regenerateStitches();
  }

  move(dx, dy) {
    this.centerX += dx;
    this.centerY += dy;
    this.regenerateStitches();
  }

  toPolygon() {
    // Convert circle to polygon format for stitch generation
    const circumference = 2 * Math.PI * this.radius;
    const numPoints = Math.max(16, Math.ceil(circumference / this.segmentLength));
    const points = [];
    for (let i = 0; i <= numPoints; i++) {
      const angle = (i / numPoints) * 2 * Math.PI;
      const x = this.centerX + this.radius * Math.cos(angle);
      const y = this.centerY + this.radius * Math.sin(angle);
      points.push([x, y]);
    }
    return [[[...points]]];
  }

  regenerateStitches() {
    const multiPoly = this.toPolygon();

    // Generate parallel stitches
    const parallelStitches = [];
    if (this.fillColor && this.fillType === 'parallel') {
      const bounds = {
        minX: this.centerX - this.radius,
        maxX: this.centerX + this.radius,
        minY: this.centerY - this.radius,
        maxY: this.centerY + this.radius
      };

      this.generateParallelStitches(bounds, multiPoly);
      if (this.stitchLists.length > 0) {
        this.stitchLists.forEach(list => {
          list.color = this.fillColor;
          parallelStitches.push(list);
        });
      }
    }

    const contourStitches = this.generateContourStitches(multiPoly);
    this.stitchLists = [...parallelStitches, ...contourStitches];
  }

  /**
   * Render the circle on the canvas.
   * If selected, it will be drawn in red with a thicker line.
   */
  renderDrawing(ctx, transform, globalStitchIndex, stitchOffset) {
    // Don't render until both points are set
    if (!this.m_hasSecondPoint) return;
    
    const bounds = {
      minX: this.centerX - this.radius,
      maxX: this.centerX + this.radius,
      minY: this.centerY - this.radius,
      maxY: this.centerY + this.radius
    };

    this.drawFill(ctx, transform, () => {
      ctx.arc(this.centerX, this.centerY, this.radius, 0, 2 * Math.PI);
    }, bounds, globalStitchIndex, stitchOffset);

    // Always draw stitches if they exist
    this.drawStitches(ctx, transform, globalStitchIndex, stitchOffset);

    ctx.save();
    ctx.beginPath();
    ctx.arc(this.centerX, this.centerY, this.radius, 0, 2 * Math.PI);
    // Use red and a thicker line if the circle is selected
    ctx.strokeStyle = this.isSelected ? 'red' : this.color;
    ctx.lineWidth = this.isSelected ? (2 / transform.scale) : (1 / transform.scale);
    ctx.stroke();
    ctx.restore();
  }

  drawPoint(ctx, x, y, color, transform) {
    ctx.beginPath();
    const radius = 5 / transform.scale;
    ctx.arc(x, y, radius, 0, 2 * Math.PI);
    ctx.fillStyle = color;
    ctx.fill();
  }

  renderActivePoints(ctx, transform) {
    if (this.centerX && this.centerY) {
      this.drawPoint(ctx, this.centerX, this.centerY, 'red', transform);
      this.drawPoint(ctx, this.centerX + this.radius, this.centerY, 'red', transform);
    }
  }

  /**
   * Checks if a point is on or near the circle's edge.
   * @param {number} px - The x-coordinate of the point to check.
   * @param {number} py - The y-coordinate of the point to check.
   * @param {object} transform - The current canvas transform, used for scaling the tolerance.
   * @returns {boolean} - True if the point is on the edge, false otherwise.
   */
  isPointInside(px, py, transform) {
    // Define a click tolerance in screen pixels, converted to world space.
    const tolerance = 5 / transform.scale;

    // Calculate the distance from the click point to the circle's center.
    const distance = Math.sqrt(Math.pow(px - this.centerX, 2) + Math.pow(py - this.centerY, 2));

    // Check if the distance is within the tolerance of the radius.
    // This means the click is on or very near the circle's circumference.
    return Math.abs(distance - this.radius) < tolerance;
  }

  setIsDrawing(dr) { this.m_isDrawing = dr }

  isDrawing() { return this.m_isDrawing }

  isComplete() {
    return this.m_hasSecondPoint;
  }

  AddPoint(point) {
    this.centerX = point.x;
    this.centerY = point.y;
    this.m_isDrawing = true;
    this.m_hasSecondPoint = false;
    return false;
  }

  AddSecondPoint(point) {
    const dx = point.x - this.centerX;
    const dy = point.y - this.centerY;
    const radius = Math.sqrt(dx * dx + dy * dy);
    this.radius = radius;
    this.m_hasSecondPoint = true;
    this.regenerateStitches(); // Generate stitches when shape is completed
    return false;
  }

  // This method seems redundant with renderDrawing but keeping it as it was in the original file.
  renderPreview(ctx, transform) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(this.centerX, this.centerY, this.radius, 0, 2 * Math.PI);
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 1 / transform.scale;
    ctx.stroke();
    ctx.restore();
  }

  // Handle mouse events (e.g., resizing or moving the circle)
  handleMouseEvent(event) {
    // Placeholder for mouse event handling logic
    console.log('Mouse event:', event);
  }
}